/**
 * Fixed-cadence reset projection for quota windows whose reset recurs on a
 * fixed weekly schedule per account (Weekly, WeeklyFable). The live API resets
 * for an account land on the same weekday and time every week (ryn.ai.data:
 * Thu 21:00Z on 09-03, 09-17, 09-24), so once the last API-sourced reset has
 * passed, the next one is that reset plus whole weeks. The 5 hour window has
 * no fixed cadence (it opens on the first message after a reset) and is never
 * projected.
 */
export const WEEKLY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** How far back an API reset may be and still anchor a projection. Beyond
 * this the account's cadence may have moved, so the estimate stands. */
export const MAX_PROJECTION_PERIODS = 4;

/** Rolls `lastResetMs` forward by whole `periodMs` until it is after `now`. */
export function projectFixedCadenceReset(
  lastResetMs: number,
  periodMs: number,
  now: number
): number {
  if (lastResetMs > now) return lastResetMs;
  const periods = Math.floor((now - lastResetMs) / periodMs) + 1;
  return lastResetMs + periods * periodMs;
}

/**
 * Next reset projected from the LATEST past API reset, or null when there is
 * none recent enough to trust. Input is epoch ms (null entries ignored).
 */
export function projectFromLastApiReset(
  apiResetMs: (number | null)[],
  periodMs: number,
  now: number
): string | null {
  const past = apiResetMs.filter(
    (ms): ms is number =>
      ms !== null && ms <= now && now - ms <= MAX_PROJECTION_PERIODS * periodMs
  );
  if (past.length === 0) return null;
  const projected = projectFixedCadenceReset(Math.max(...past), periodMs, now);
  return new Date(projected).toISOString();
}
